import type { Process } from 'shared/domain/process/types/model';
import type { PermissionsModel } from '../types/model';
import { UserRole } from 'shared/types/userRole';

export interface ProjectPermissionSnapshot {
  sites: string[];
  processes: string[];
  role: UserRole;
}

export interface NormalizeAndPersistUserOptions {
  organizationId?: string;
  projectId?: string;
  editedUserId?: string;
  sentProjectPermission?: ProjectPermissionSnapshot;
  warnIfMissingSentProjectPermission?: boolean;
}

function toProcessId(process: Process | string | { _id: string }): string {
  if (typeof process === 'string') {
    return process;
  }

  return process._id;
}

export function normalizeProcessIds(
  processes: readonly (Process | string | { _id: string })[]
): string[] {
  return processes.map(toProcessId);
}

export function extractProjectPermissionFromPermissionsModel(
  permissions: PermissionsModel | undefined,
  projectId: string
): ProjectPermissionSnapshot | undefined {
  if (!permissions?.clients?.length) {
    return undefined;
  }

  for (const client of permissions.clients) {
    if ('role' in client && client.role === UserRole.organizationAdmin) {
      return {
        sites: [],
        processes: [],
        role: UserRole.organizationAdmin,
      };
    }

    if (!('projects' in client) || !client.projects) {
      continue;
    }

    const projectPermission = client.projects.find(
      (project) => project._id === projectId
    );
    if (!projectPermission) {
      continue;
    }

    if (projectPermission.role === UserRole.visible_no_access) {
      return {
        sites: [],
        processes: [],
        role: UserRole.visible_no_access,
      };
    }

    if (projectPermission.role === UserRole.projectAdmin) {
      return {
        sites: [],
        processes: [],
        role: UserRole.projectAdmin,
      };
    }

    return {
      sites: projectPermission.sites ?? [],
      processes: normalizeProcessIds(projectPermission.processes ?? []),
      role: projectPermission.role,
    };
  }

  return undefined;
}
